/**
 * Solio Cart Drawer — site-wide.
 *
 * - Open: any [data-cart-drawer-trigger] OR <a href="/cart"> click.
 * - Close: backdrop, ESC, × button, "Weiter einkaufen" link.
 * - Adds: intercepts <form action="/cart/add"> submits, POSTs to /cart/add.js,
 *   then re-fetches /cart.js, re-renders the drawer body + footer, opens.
 * - Qty +/-: POST /cart/change.js — response is the full cart; re-render.
 * - Remove: POST /cart/change.js with quantity:0 — re-render.
 * - Cross-tab sync: window 'storage' listener on key 'solio_cart_synced'.
 *
 * Accessibility: focus trap, ESC closes, focus returns to triggering element.
 */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-solio-cart-drawer]');
  if (!drawer) return;

  /* ---- locale ---- */
  var captionEl       = drawer.querySelector('[data-shipping-caption]');
  var TPL_BELOW       = captionEl ? captionEl.getAttribute('data-tpl-below') : '{{ amount }} to free shipping';
  var TPL_UNLOCKED    = captionEl ? captionEl.getAttribute('data-tpl-unlocked') : '✓ Free shipping unlocked';
  var ERROR_ADD       = drawer.getAttribute('data-error-add') || 'Variante nicht verfügbar.';
  var ERROR_NETWORK   = drawer.getAttribute('data-error-network') || 'Verbindungsfehler — bitte erneut versuchen.';

  var FREE_SHIP_CENTS = parseInt(drawer.getAttribute('data-free-shipping-threshold'), 10) || 5000;

  /* ---- DOM refs ---- */
  var backdrop       = drawer.querySelector('[data-cart-drawer-backdrop]');
  var bodyEl         = drawer.querySelector('[data-cart-drawer-body]');
  var footerEl       = drawer.querySelector('[data-cart-drawer-footer]');
  var subtotalEl     = drawer.querySelector('[data-cart-subtotal]');
  var fillEl         = drawer.querySelector('[data-shipping-fill]');
  var errorEl        = drawer.querySelector('[data-cart-drawer-error]');

  var lastTrigger    = null;
  var state          = { cart: null, busy: false };

  /* ============================================================
     OPEN / CLOSE + FOCUS TRAP
     ============================================================ */

  function focusable() {
    return Array.prototype.slice.call(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) { return el.offsetParent !== null; });
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var f = focusable();
    if (f.length === 0) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function open(trigger) {
    lastTrigger = trigger || document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('hidden');
    document.body.classList.add('solio-cart-drawer-open');
    document.addEventListener('keydown', onKeydown);
    requestAnimationFrame(function () {
      var closeBtn = drawer.querySelector('[data-cart-drawer-close]');
      if (closeBtn) closeBtn.focus();
    });
    // Refresh from server on every open so cross-tab / external mutations
    // are reflected immediately.
    fetchCart().then(render).catch(function () { /* keep server-rendered fallback */ });
  }

  function close() {
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('hidden', '');
    document.body.classList.remove('solio-cart-drawer-open');
    document.removeEventListener('keydown', onKeydown);
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    lastTrigger = null;
  }

  /* ============================================================
     GLOBAL TRIGGERS
     ============================================================ */

  document.addEventListener('click', function (e) {
    // Explicit triggers
    var trigger = e.target.closest('[data-cart-drawer-trigger]');
    if (trigger) {
      e.preventDefault();
      open(trigger);
      return;
    }
    // Any link to /cart
    var cartLink = e.target.closest('a');
    if (cartLink) {
      var href = cartLink.getAttribute('href') || '';
      if (href === '/cart' || href.indexOf('/cart?') === 0) {
        e.preventDefault();
        open(cartLink);
        return;
      }
    }
    // Close triggers (× button, "Weiter einkaufen", empty CTA)
    if (e.target.closest('[data-cart-drawer-close]')) {
      // Empty-state CTA is also data-cart-drawer-close but is an <a> — let it navigate AND close.
      // Buttons stay buttons. We close in either case.
      close();
      // Don't preventDefault — anchors still navigate.
    }
    // Backdrop
    if (e.target === backdrop) {
      close();
    }
  });

  /* ============================================================
     INTERCEPT ADD-TO-CART FORM SUBMITS
     ============================================================ */

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form');
    if (!form) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    e.preventDefault();
    handleAdd(form);
  });

  function handleAdd(form) {
    if (state.busy) return;
    state.busy = true;
    hideError();
    // PDP inline error region (if this form lives inside the product hero).
    var pdpErrorEl = null;
    var pdpRoot = form.closest('[data-solio-product-hero]');
    if (pdpRoot) {
      pdpErrorEl = pdpRoot.querySelector('[data-cart-error]');
      if (pdpErrorEl) {
        pdpErrorEl.setAttribute('hidden', '');
        pdpErrorEl.textContent = '';
      }
    }

    var fd = new FormData(form);
    fetch('/cart/add.js', { method: 'POST', body: fd, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            throw new Error((err && err.description) || ERROR_ADD);
          }).catch(function () { throw new Error(ERROR_ADD); });
        }
        return r.json();
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        render(cart);
        broadcast();
        open(form);
      })
      .catch(function (err) {
        var msg = (err && err.message) ? err.message : ERROR_ADD;
        // Prefer inline PDP error per spec; fall back to the drawer's error.
        if (pdpErrorEl) {
          pdpErrorEl.textContent = msg;
          pdpErrorEl.removeAttribute('hidden');
        } else {
          showError(msg);
          open(form);
        }
      })
      .then(function () { state.busy = false; });
  }

  /* ============================================================
     QTY + REMOVE  (delegated, works on JS-rendered items)
     ============================================================ */

  drawer.addEventListener('click', function (e) {
    var minus = e.target.closest('[data-qty-minus]');
    var plus  = e.target.closest('[data-qty-plus]');
    var rem   = e.target.closest('[data-remove]');
    if (!minus && !plus && !rem) return;

    var li = e.target.closest('[data-cart-item]');
    if (!li) return;
    var key = li.getAttribute('data-line-key');
    if (!key) return;

    var valueEl = li.querySelector('[data-qty-value]');
    var current = valueEl ? parseInt(valueEl.textContent, 10) || 1 : 1;
    var next;
    if (rem) next = 0;
    else if (plus) next = current + 1;
    else next = Math.max(0, current - 1);

    // Optimistic UI
    if (valueEl && next > 0) valueEl.textContent = String(next);

    updateLine(key, next, current, valueEl);
  });

  function updateLine(key, quantity, prev, valueEl) {
    if (state.busy) return;
    state.busy = true;
    hideError();
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
      credentials: 'same-origin'
    })
      .then(function (r) { if (!r.ok) throw new Error('network'); return r.json(); })
      .then(function (cart) {
        render(cart);
        broadcast();
      })
      .catch(function () {
        if (valueEl && prev != null) valueEl.textContent = String(prev);
        showError(ERROR_NETWORK);
      })
      .then(function () { state.busy = false; });
  }

  /* ============================================================
     FETCH + RENDER
     ============================================================ */

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { state.cart = cart; return cart; });
  }

  function moneyFormat(cents) {
    // Use Shopify's window.Shopify.formatMoney if exposed; otherwise fallback.
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && Shopify.money_format) {
      return Shopify.formatMoney(cents, Shopify.money_format);
    }
    // Fallback: EUR formatting without trailing zeros for whole values
    var euros = cents / 100;
    var hasCents = Math.round(cents) % 100 !== 0;
    return '€' + (hasCents ? euros.toFixed(2).replace('.', ',') : Math.round(euros));
  }

  function escapeHTML(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  function renderItem(item) {
    var compare = '';
    if (item.original_line_price > item.final_line_price) {
      compare = '<span class="solio-cart-drawer__item-compare">' +
        escapeHTML(moneyFormat(item.original_line_price)) +
      '</span>';
    }
    var variantHtml = '';
    if (item.variant_title && item.variant_title.indexOf('Default') === -1) {
      variantHtml = '<p class="solio-cart-drawer__item-variant">' + escapeHTML(item.variant_title) + '</p>';
    }
    var img = item.image
      ? '<img class="solio-cart-drawer__item-image" src="' + escapeHTML(item.image) +
        '" width="80" height="80" loading="lazy" alt="' + escapeHTML(item.product_title) + '">'
      : '<div class="solio-cart-drawer__item-image solio-cart-drawer__item-image--placeholder"></div>';

    return (
      '<li class="solio-cart-drawer__item" data-cart-item data-line-key="' + escapeHTML(item.key) + '">' +
        '<a class="solio-cart-drawer__item-image-link" href="' + escapeHTML(item.url) + '">' + img + '</a>' +
        '<div class="solio-cart-drawer__item-content">' +
          '<a class="solio-cart-drawer__item-title" href="' + escapeHTML(item.url) + '">' +
            escapeHTML(item.product_title) +
          '</a>' +
          variantHtml +
          '<p class="solio-cart-drawer__item-price">' +
            compare +
            '<span class="solio-cart-drawer__item-final">' + escapeHTML(moneyFormat(item.final_line_price)) + '</span>' +
          '</p>' +
        '</div>' +
        '<div class="solio-cart-drawer__item-controls">' +
          '<div class="solio-cart-drawer__qty" role="group">' +
            '<button type="button" class="solio-cart-drawer__qty-btn" data-qty-minus' +
              (item.quantity <= 1 ? ' disabled' : '') + '>−</button>' +
            '<span class="solio-cart-drawer__qty-value" data-qty-value>' + item.quantity + '</span>' +
            '<button type="button" class="solio-cart-drawer__qty-btn" data-qty-plus>+</button>' +
          '</div>' +
          '<button type="button" class="solio-cart-drawer__remove" data-remove>Entfernen</button>' +
        '</div>' +
      '</li>'
    );
  }

  function render(cart) {
    if (!cart) return;

    /* ---- body ---- */
    if (cart.item_count > 0) {
      var html = '<ul class="solio-cart-drawer__items" data-cart-items>';
      for (var i = 0; i < cart.items.length; i++) html += renderItem(cart.items[i]);
      html += '</ul>';
      bodyEl.innerHTML = html;
      footerEl.classList.remove('is-empty');
    } else {
      // Re-render empty state. We keep the existing markup for empty state so
      // localization stays intact; the simplest way is to clone the original
      // template by re-fetching the snippet via section rendering. For
      // simplicity, we just rebuild a minimal empty block (locale strings
      // come from data attrs on the original empty block if available).
      var emptyTpl = drawer.querySelector('[data-cart-empty]');
      if (emptyTpl) {
        bodyEl.innerHTML = emptyTpl.outerHTML;
      }
      footerEl.classList.add('is-empty');
    }

    /* ---- subtotal ---- */
    if (subtotalEl) subtotalEl.textContent = moneyFormat(cart.total_price);

    /* ---- shipping progress ---- */
    var pct = Math.min(100, (cart.total_price / FREE_SHIP_CENTS) * 100);
    if (fillEl) fillEl.style.width = pct + '%';
    if (captionEl) {
      if (cart.total_price >= FREE_SHIP_CENTS) {
        captionEl.textContent = TPL_UNLOCKED;
        captionEl.classList.add('is-unlocked');
      } else {
        var remaining = FREE_SHIP_CENTS - cart.total_price;
        captionEl.textContent = TPL_BELOW.replace('{{ amount }}', moneyFormat(remaining));
        captionEl.classList.remove('is-unlocked');
      }
    }

    /* ---- cart count badge(s) in header/etc ---- */
    var counts = document.querySelectorAll('[data-cart-count]');
    for (var c = 0; c < counts.length; c++) counts[c].textContent = '(' + cart.item_count + ')';
  }

  /* ============================================================
     ERROR
     ============================================================ */

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.removeAttribute('hidden');
  }

  function hideError() {
    if (!errorEl) return;
    errorEl.setAttribute('hidden', '');
    errorEl.textContent = '';
  }

  /* ============================================================
     CROSS-TAB SYNC
     ============================================================ */

  function broadcast() {
    try {
      // Bump a value so other tabs receive a 'storage' event.
      localStorage.setItem('solio_cart_synced', String(Date.now()));
    } catch (e) {}
  }

  window.addEventListener('storage', function (e) {
    if (e.key !== 'solio_cart_synced') return;
    fetchCart().then(render).catch(function () {});
  });
})();
